/**
 * RechargeModal — 用户充值弹窗
 * 
 * 流程：
 *  1. 用户选择套餐金额或输入自定义金额
 *  2. 点击充值 → 调 /api/payment/create → 获取二维码
 *  3. 展示二维码 → 轮询 /api/payment/status/:no
 *  4. 支付成功 → 更新积分 → 关闭弹窗
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, CreditCard, Sparkle, QrCode, Check, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import api from '../api';

// 步骤枚举
const STEP = {
  SELECT: 'select',   // 选择金额
  PAY: 'pay',         // 展示二维码
  SUCCESS: 'success',  // 支付成功
};

export default function RechargeModal({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(STEP.SELECT);
  const [packages, setPackages] = useState([]);
  const [rate, setRate] = useState(10);
  const [customEnabled, setCustomEnabled] = useState(true);
  const [customMin, setCustomMin] = useState(1);
  const [customMax, setCustomMax] = useState(500);
  const [qrHint, setQrHint] = useState('请使用微信扫码支付');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  // 二维码相关
  const [, setOrderNo] = useState('');
  const [qrcodeUrl, setQrcodeUrl] = useState('');
  const [orderCredits, setOrderCredits] = useState(0);
  const [orderAmount, setOrderAmount] = useState(0);
  // 轮询
  const pollRef = useRef(null);
  const [, setPollCount] = useState(0);

  // 加载套餐列表
  useEffect(() => {
    if (!open) return;
    setStep(STEP.SELECT);
    setError('');
    setSelectedAmount(null);
    setCustomAmount('');
    setIsCustom(false);
    setLoading(true);

    api.get('/api/payment/packages')
      .then(data => {
        setPackages(data.packages || []);
        setRate(data.rate || 10);
        setCustomEnabled(data.custom_enabled !== false);
        setCustomMin(data.custom_min || 1);
        setCustomMax(data.custom_max || 500);
        setQrHint(data.qr_hint || '请使用微信扫码支付');
      })
      .catch(err => {
        setError(err.message || '加载充值信息失败');
      })
      .finally(() => setLoading(false));
  }, [open]);

  // 清理轮询
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // 清理轮询 when modal closed
  useEffect(() => {
    if (!open && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [open]);

  const getAmount = () => {
    if (isCustom) {
      const val = parseFloat(customAmount);
      return isNaN(val) ? 0 : val;
    }
    return selectedAmount || 0;
  };

  const getCredits = () => {
    const amt = getAmount();
    if (!isCustom && amt > 0) {
      const pkg = packages.find(p => p.amount === amt);
      if (pkg) return pkg.credits;
    }
    return Math.round(amt * rate);
  };

  // 创建订单
  const handleCreate = useCallback(async () => {
    const amount = getAmount();
    if (amount <= 0) {
      setError('请选择或输入充值金额');
      return;
    }
    if (isCustom && (amount < customMin || amount > customMax)) {
      setError(`自定义金额需在 ¥${customMin} ~ ¥${customMax} 之间`);
      return;
    }
    setError('');
    setCreating(true);
    try {
      const data = await api.post('/api/payment/create', { amount });
      setOrderNo(data.order_no);
      setQrcodeUrl(data.qrcode_url || data.url || '');
      setOrderCredits(data.credits);
      setOrderAmount(data.amount);
      setStep(STEP.PAY);
      setPollCount(0);

      // 开始轮询
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.get(`/api/payment/status/${data.order_no}`);
          setPollCount(c => c + 1);
          if (status.status === 'paid') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStep(STEP.SUCCESS);
            // 通知父组件刷新积分
            if (onSuccess) onSuccess(status.credits);
          }
        } catch (e) {
          // 轮询失败不处理
        }
      }, 3000);
    } catch (err) {
      setError(err.message || '创建订单失败');
    } finally {
      setCreating(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustom, customAmount, selectedAmount, rate, customMin, customMax, onSuccess]);

  if (!open) return null;

  const amount = getAmount();
  const credits = getCredits();

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn 0.2s ease'
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--toolbar-bg)', borderRadius: 16, width: 440, maxWidth: '92vw',
        maxHeight: '85vh', overflow: 'auto',
        border: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-panel)'
      }} onClick={e => e.stopPropagation()}>

        {/* 标题栏 */}
        <div style={{
          padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid var(--border-subtle)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {step === STEP.PAY && (
              <button onClick={() => {
                if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
                setStep(STEP.SELECT);
              }} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 0, display: 'flex'
              }}>
                <ArrowLeft size={18} />
              </button>
            )}
            <CreditCard size={18} style={{ color: '#eab308' }} />
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-body)' }}>
              {step === STEP.SELECT ? '积分充值' : step === STEP.PAY ? '扫码支付' : '充值成功'}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2
          }}>
            <X size={18} />
          </button>
        </div>

        {/* 内容区 */}
        <div style={{ padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: 8, fontSize: 13 }}>加载中...</p>
            </div>
          ) : step === STEP.SELECT ? (
            /* ===== 步骤1: 选择金额 ===== */
            <div>
              {/* 套餐网格 */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
                marginBottom: 16
              }}>
                {packages.map(pkg => {
                  const active = !isCustom && selectedAmount === pkg.amount;
                  return (
                    <button key={pkg.amount} onClick={() => {
                      setSelectedAmount(pkg.amount);
                      setIsCustom(false);
                      setError('');
                    }} style={{
                      padding: '14px 8px', borderRadius: 10, cursor: 'pointer',
                      background: active ? 'rgba(234,179,8,0.15)' : 'var(--modal-input-bg)',
                      border: `1.5px solid ${active ? '#eab308' : 'var(--border-subtle)'}`,
                      transition: 'all 0.15s',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    }}>
                      <span style={{
                        fontSize: 18, fontWeight: 700,
                        color: active ? '#facc15' : 'var(--text-body)'
                      }}>¥{pkg.amount}</span>
                      <span style={{
                        fontSize: 11, color: active ? '#eab308' : '#64748b',
                        display: 'flex', alignItems: 'center', gap: 2
                      }}>
                        <Sparkle size={10} />
                        {pkg.credits} 积分
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* 自定义金额 */}
              {customEnabled && (
                <div style={{
                  padding: 12, borderRadius: 10,
                  background: isCustom ? 'rgba(234,179,8,0.08)' : 'var(--modal-input-bg)',
                  border: `1px solid ${isCustom ? 'rgba(234,179,8,0.3)' : 'var(--border-subtle)'}`,
                  transition: 'border-color 0.15s, background 0.15s', marginBottom: 16,
                  cursor: 'pointer'
                }} onClick={() => { if (!isCustom) { setIsCustom(true); setSelectedAmount(null); } }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>自定义金额</span>
                    <span style={{ fontSize: 11, color: '#475569' }}>（¥{customMin} ~ ¥{customMax}）</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36 }}>
                    <span style={{ color: isCustom ? '#facc15' : '#475569', fontSize: 18, fontWeight: 600, transition: 'color 0.15s' }}>¥</span>
                    <input
                      type="number"
                      min={customMin}
                      max={customMax}
                      value={customAmount}
                      onChange={e => { setCustomAmount(e.target.value); setError(''); if (!isCustom) { setIsCustom(true); setSelectedAmount(null); } }}
                      onFocus={() => { if (!isCustom) { setIsCustom(true); setSelectedAmount(null); } }}
                      onClick={e => e.stopPropagation()}
                      placeholder={`${customMin} ~ ${customMax}`}
                      style={{
                        flex: 1, padding: '8px 12px', height: 36, boxSizing: 'border-box',
                        background: isCustom ? 'rgba(0,0,0,0.3)' : 'var(--modal-input-bg)',
                        border: `1px solid ${isCustom ? 'rgba(234,179,8,0.3)' : 'var(--border-subtle)'}`,
                        borderRadius: 6, color: isCustom ? '#facc15' : '#64748b', fontSize: 16, fontWeight: 600,
                        outline: 'none', caretColor: '#facc15',
                        transition: 'border-color 0.15s, background 0.15s, color 0.15s',
                      }}
                    />
                    <span style={{
                      fontSize: 12, color: '#eab308', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 3,
                      opacity: (customAmount && parseFloat(customAmount) > 0) ? 1 : 0,
                      transition: 'opacity 0.15s', minWidth: 70
                    }}>
                      <Sparkle size={10} /> {customAmount && parseFloat(customAmount) > 0 ? Math.round(parseFloat(customAmount) * rate) : 0} 积分
                    </span>
                  </div>
                </div>
              )}

              {/* 错误提示 */}
              {error && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                  background: 'rgba(239,68,68,0.1)', borderRadius: 8, marginBottom: 12,
                  border: '1px solid rgba(239,68,68,0.2)',
                }}>
                  <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: '#fca5a5' }}>{error}</span>
                </div>
              )}

              {/* 汇总 + 充值按钮 */}
              <div style={{
                padding: 12, borderRadius: 10, background: 'rgba(234,179,8,0.06)',
                border: '1px solid rgba(234,179,8,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 16
              }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>充值金额</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: amount > 0 ? '#facc15' : '#475569' }}>
                    ¥{amount > 0 ? amount : '—'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>获得积分</div>
                  <div style={{
                    fontSize: 20, fontWeight: 700, color: credits > 0 ? '#facc15' : '#475569',
                    display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end'
                  }}>
                    <Sparkle size={16} />
                    {credits > 0 ? credits : '—'}
                  </div>
                </div>
              </div>

              <button
                onClick={handleCreate}
                disabled={creating || amount <= 0}
                style={{
                  width: '100%', padding: '12px 0',
                  background: amount > 0
                    ? 'linear-gradient(135deg, #eab308, #ca8a04)'
                    : 'var(--modal-input-bg)',
                  border: 'none', borderRadius: 10,
                  cursor: amount > 0 && !creating ? 'pointer' : 'not-allowed',
                  color: amount > 0 ? '#0f172a' : '#475569',
                  fontSize: 14, fontWeight: 600,
                  opacity: creating ? 0.6 : 1,
                  transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                }}
              >
                {creating ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    正在创建订单...
                  </>
                ) : (
                  <>
                    <QrCode size={16} />
                    微信扫码支付
                  </>
                )}
              </button>

              <p style={{ textAlign: 'center', fontSize: 11, color: '#475569', marginTop: 10, marginBottom: 0 }}>
                支付完成后积分将自动到账
              </p>
            </div>

          ) : step === STEP.PAY ? (
            /* ===== 步骤2: 展示二维码 ===== */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* 二维码 */}
              {qrcodeUrl ? (
                <div style={{
                  padding: 16, background: '#fff', borderRadius: 12,
                  display: 'inline-block', marginBottom: 14
                }}>
                  <img
                    src={qrcodeUrl}
                    alt="微信支付二维码"
                    style={{ width: 200, height: 200, display: 'block' }}
                  />
                </div>
              ) : (
                <div style={{
                  padding: 40, color: '#ef4444', fontSize: 13,
                  background: 'rgba(239,68,68,0.1)', borderRadius: 12, marginBottom: 14
                }}>
                  <AlertCircle size={24} style={{ marginBottom: 8 }} />
                  <p style={{ margin: 0 }}>二维码加载失败，请重试</p>
                </div>
              )}

              {/* 金额 → 积分 */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '6px 18px', borderRadius: 16,
                background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.15)',
                marginBottom: 14
              }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#facc15' }}>¥{orderAmount}</span>
                <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>→</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#eab308', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Sparkle size={12} /> {orderCredits} 积分
                </span>
              </div>

              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
                {qrHint}
              </p>
              <p style={{ fontSize: 11, color: '#475569', marginBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                等待支付中...
              </p>
            </div>

          ) : (
            /* ===== 步骤3: 支付成功 ===== */
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: 60, height: 60, borderRadius: '50%',
                background: 'rgba(16,185,129,0.15)', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', marginBottom: 16
              }}>
                <Check size={30} style={{ color: '#10b981' }} />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>
                充值成功！
              </h3>
              <p style={{
                fontSize: 14, color: '#eab308', margin: '0 0 4px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
              }}>
                <Sparkle size={14} /> +{orderCredits} 积分
              </p>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 20px' }}>
                积分已自动到账
              </p>
              <button
                onClick={onClose}
                style={{
                  padding: '10px 32px', background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none', borderRadius: 8, cursor: 'pointer',
                  color: '#fff', fontSize: 13, fontWeight: 600
                }}
              >
                完成
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 动画 keyframes */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
      `}</style>
    </div>
  );
}
